/**
 * Default list columns, derived from a type's JSON Schema (GDN-2723).
 *
 * This is the fallback every unregistered type gets. A type that wants more
 * columns, better labels, or custom cells registers `columns` instead — that's
 * what the slot exists for, so the default stays deliberately conservative and
 * readable rather than dumping every field into the table.
 */

import type { ResourceSchema } from '../resource-renderer-types';
import type { ResourceColumnDef, ResourceRow } from './types';

// Provenance / tenancy / timestamp columns are noise in a browse table — the
// detail view shows them. `id` is rendered as the link column separately.
const SYSTEM_COLUMNS = new Set([
  'id',
  'scope',
  'organization_id',
  'user_id',
  'created_by_principal_id',
  'created_by_principal_kind',
  'created_at',
  'updated_at',
]);

const MAX_DERIVED_COLUMNS = 8;

export function humanize(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function isIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value);
}

/** Compact single-cell rendering of an arbitrary scalar/complex value. */
export function formatCell(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    return isIsoDate(value) ? new Date(value).toLocaleDateString() : value;
  }
  if (Array.isArray(value)) return `[${value.length}]`;
  return '{…}';
}

/**
 * Columns for a type with no registered `columns`: schema field order when a
 * schema is available, else whatever keys the rows actually carry.
 *
 * Sorting is off for derived columns — the generic list endpoint dispatches to
 * each type's canonical `list`, which takes no sort parameter, so offering a
 * sort affordance here would be a control that silently does nothing.
 */
export function deriveColumns(
  items: ResourceRow[],
  schema?: ResourceSchema,
  resourceType?: string,
): ResourceColumnDef[] {
  const fromSchema = Object.entries(schema?.json_schema?.properties ?? {});
  const candidates: [string, { title?: string } | undefined][] =
    fromSchema.length > 0
      ? fromSchema
      : Array.from(new Set(items.flatMap(item => Object.keys(item)))).map(
          key => [key, undefined],
        );

  // The list endpoint copies each row's primary key onto `id` for the link
  // column, so a model that also exposes its natural key by name (encounter →
  // `encounter_id`) would otherwise render the same value in two columns.
  const naturalKey = resourceType ? `${resourceType}_id` : null;

  return candidates
    .filter(([key]) => !SYSTEM_COLUMNS.has(key) && key !== naturalKey)
    .slice(0, MAX_DERIVED_COLUMNS)
    .map(([key, prop]) => ({
      key,
      label: prop?.title ?? humanize(key),
      sortable: false,
    }));
}
